import React, { useEffect, useRef } from "react";
import { motion, useInView, useAnimation, useSpring, useMotionValue } from "framer-motion";
import { Clock, TrendingUp, Target, Compass } from "lucide-react";

const About = () => {
    return (
        <section
            id="about"
            className="
                relative w-full py-24 px-4 sm:px-10 lg:px-20
                overflow-hidden font-poppins
            "
        >
            <div
                className="
                    relative z-10 mx-auto
                    w-[95%] md:w-[90%] lg:w-[95%] xl:w-[80%] 2xl:w-[75%]
                    grid lg:grid-cols-2 gap-16 lg:gap-12 xl:gap-24 items-center
                "
            >
                {/* LEFT CONTENT */}
                <ContentSection />

                {/* RIGHT IMAGE + FEATURES */}
                <RightImagePanel />
            </div>
        </section>
    );
};

export default About;

/* ================================================================
   LEFT — CONTENT BLOCK
================================================================= */
function ContentSection() {
    const ref = useRef(null);
    const isInView = useInView(ref, { amount: 0.2, once: true });
    const controls = useAnimation();

    useEffect(() => {
        if (isInView) controls.start("visible");
    }, [controls, isInView]);

    // --- STATISTICS DATA ---
    const stats = [
        { label: "Students Mentored", value: 2000, suffix: "+" },
        { label: "Seminars Completed", value: 200, suffix: "+" },
        { label: "Positive Reviews", value: 100, suffix: "+" },
    ];

    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={{
                hidden: { opacity: 0, x: -50 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
            }}
            className="space-y-8"
        >
            {/* Badge */}
            <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                <span className="text-xs font-bold tracking-widest text-primary uppercase">
                    Who We Are
                </span>
            </motion.div>

            {/* Heading */}
            <motion.h2
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold text-dark dark:text-light leading-[1.1]"
            >
                <span className="block leading-[1.2]">Learn the Art of</span>
                <span className="block leading-[1.2] text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                    Smarter Trading
                </span>
            </motion.h2>

            {/* Paragraph */}
            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed border-l-4 border-primary/30 pl-6">
                We are a financial services company specializing in forex trading, education, and market-driven investment solutions.
                <br /><br />
                Our mission is to empower traders with the <span className="text-dark dark:text-light font-semibold">knowledge, tools, and strategies</span> they need to succeed.
            </p>

            {/* --- NEW STATISTICS SECTION --- */}
            <div className="grid grid-cols-3 gap-4 border-t border-gray-200 dark:border-white/10 pt-8">
                {stats.map((stat, index) => (
                    <div key={index} className="flex flex-col">
                        <div className="text-2xl sm:text-3xl font-bold text-primary flex items-baseline">
                            <AnimatedCounter value={stat.value} />
                            <span className="text-lg ml-0.5">{stat.suffix}</span>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mt-1">
                            {stat.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* CTA Button */}
            {/*<div className="pt-2">*/}
            {/*    <button className="group flex items-center gap-2 text-primary font-bold hover:gap-4 transition-all duration-300">*/}
            {/*        Discover Our Vision*/}
            {/*        <ArrowRight className="w-5 h-5" />*/}
            {/*    </button>*/}
            {/*</div>*/}
        </motion.div>
    );
}

/* ================================================================
   RIGHT — IMAGE PANEL + FEATURES
================================================================= */
function RightImagePanel() {
    return (
        <div className="flex flex-col gap-8 w-full">

            {/* BIG IMAGE */}
            <div className="relative group rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 border border-gray-200 dark:border-white/10">
                <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                <ScrollImage
                    src="/assets/bangalore/bangalore2.jpg"
                    className="h-64 sm:h-80 w-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    delay={0.2}
                />
            </div>

            {/* FEATURE CARDS GRID */}
            <motion.div
                className="grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                    visible: { transition: { staggerChildren: 0.1 } }
                }}
            >
                <FeatureCard
                    icon={<Clock className="w-5 h-5 text-white" />}
                    title="Time Trade Mastery"
                    text="Precision trade timing using Vedic Maths and Time Analysis."
                />
                <FeatureCard
                    icon={<TrendingUp className="w-5 h-5 text-white" />}
                    title="Fortune Timing"
                    text="Predict major market moves ahead of time."
                />
                <FeatureCard
                    icon={<Target className="w-5 h-5 text-white" />}
                    title="Strategic Edge"
                    text="Exact entry, exit, and stop-loss for any market."
                />
                <FeatureCard
                    icon={<Compass className="w-5 h-5 text-white" />}
                    title="Astro-Math Blueprint"
                    text="Ancient wisdom meets modern trading accuracy."
                />
            </motion.div>
        </div>
    );
}

/* ================================================================
   HELPER COMPONENTS
================================================================= */

function FeatureCard({ title, text, icon }) {
    return (
        <motion.div
            className="
                group relative p-5 rounded-xl
                bg-white dark:bg-bg-dark-2
                border border-gray-100 dark:border-white/5
                shadow-sm hover:shadow-lg hover:shadow-primary/5 hover:border-primary/30
                transition-all duration-300
            "
            variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
            }}
        >
            <div className="flex items-start gap-4">
                <div className="
                    flex-shrink-0 w-10 h-10 rounded-lg
                    bg-gradient-to-br from-primary to-secondary
                    flex items-center justify-center
                    shadow-md group-hover:scale-110 transition-transform duration-300
                ">
                    {icon}
                </div>
                <div>
                    <h3 className="text-base font-bold text-dark dark:text-light mb-1 group-hover:text-primary transition-colors">
                        {title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        {text}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}

function ScrollImage({ src, delay, className }) {
    return (
        <motion.img
            src={src}
            className={className}
            initial={{ opacity: 0, scale: 1.1 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut", delay }}
        />
    );
}

/* --- ANIMATED COUNTER COMPONENT --- */
function AnimatedCounter({ value }) {
    const ref = useRef(null);
    const motionValue = useMotionValue(0);
    const springValue = useSpring(motionValue, { damping: 30, stiffness: 100 });
    const isInView = useInView(ref, { once: true, margin: "-10px" });

    useEffect(() => {
        if (isInView) {
            motionValue.set(value);
        }
    }, [isInView, value, motionValue]);

    useEffect(() => {
        return springValue.on("change", (latest) => {
            if (ref.current) {
                ref.current.textContent = Math.floor(latest).toLocaleString();
            }
        });
    }, [springValue]);

    return <span ref={ref}>0</span>;
}